import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { adminGet, adminPost, adminPut, adminPatch } from './adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const GREEN = '#0B6B55';
const MODES = [
  ['BRAND_OWNS', 'Brand / manufacturer owns', 'Passports minted by the brand; partners participate'],
  ['DISTRIBUTOR_OWNS', 'Distributor owns', 'Journey starts at distributor inward'],
  ['EACH_OWNS_LEGS', 'Each company owns its legs', 'Shared chain view only by agreement'],
];
const PARTY_LABEL = { OWNER: 'Brand (owner)', CFA: 'CFA', TRANSPORTER: 'Transporter', DISTRIBUTOR: 'Distributor', RECEIVER: 'Receiver (retailer / hospital)', PUBLIC: 'Public QR scan' };
const OWN_LEGS = [['FULL', 'Full detail'], ['SUMMARY', 'Summary'], ['NONE', '—']];
const JOURNEY = [['FULL_CHAIN', 'Full chain'], ['OWN_PLUS_PASSFAIL', 'Own legs + pass/fail of others'], ['OWN_ONLY', 'Own trips only'], ['LAST_LEG_ONLY', 'Last leg only'], ['OVERALL_STATUS_ONLY', 'Overall status only']];
const LOCS = [['CITY_LEVEL', 'City level'], ['OWN_SITES_ONLY', 'Own sites only'], ['OWN_TRIPS_ONLY', 'Own trips only'], ['NONE', 'None']];
const CERTS = [['FULL', 'Full'], ['SUMMARY', 'Summary'], ['LAST_LEG', 'Last leg'], ['NONE', 'None']];

const Sel = ({ value, options, onChange, testid }) => (
  <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)}
    className="w-full h-9 px-2 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export default function JourneySharingPage() {
  const { tenantId } = useParams();
  const [params] = useSearchParams();
  const templateId = params.get('template');
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [partners, setPartners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invite, setInvite] = useState({ mode: 'tenant', partner_tenant_id: '', guest_org_name: '', stage_roles: 'CFA warehouse' });
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(() => {
    if (templateId) {
      adminGet(`/customers/${tenantId}/visibility-rules?template_id=${templateId}`).then(setConfig).catch(e => toast.error(e.message));
    }
    adminGet(`/customers/${tenantId}/partners`).then(setPartners).catch(e => toast.error(e.message));
    adminGet('/customers-lite').then(setCustomers).catch(() => {});
  }, [tenantId, templateId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await adminPut(`/customers/${tenantId}/visibility-rules`, config);
      toast.success('Visibility rules saved');
    } catch (e) { toast.error(e.message); }
  };

  const setRule = (i, key, val) => {
    const rules = [...config.rules];
    rules[i] = { ...rules[i], [key]: val };
    setConfig({ ...config, rules });
  };

  const sendInvite = async () => {
    try {
      const body = { stage_roles: invite.stage_roles.split(',').map(x => x.trim()).filter(Boolean) };
      if (invite.mode === 'tenant') body.partner_tenant_id = invite.partner_tenant_id;
      else { body.guest_org_name = invite.guest_org_name; }
      const res = await adminPost(`/customers/${tenantId}/partners`, body);
      toast.success(`Invited ${res.partner_name}`);
      setShowInvite(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const revoke = async (link) => {
    try {
      await adminPatch(`/customers/${tenantId}/partners/${link.id}`, { status: 'REVOKED' });
      toast.success('Link revoked — access stops immediately');
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="journey-sharing-page">
      <button data-testid="back-to-journeys" onClick={() => navigate('/admin/journeys')}
        className="flex items-center gap-1.5 text-sm mb-3 hover:underline" style={{ color: '#6b7069' }}>
        <ArrowLeft size={15} /> Journey templates</button>
      <h1 className="text-4xl font-bold mb-1" style={{ color: '#1B1D1C' }}>Ownership and sharing</h1>
      <p className="text-sm mb-6" style={{ color: '#6b7069' }}>
        Set per customer. Nothing is shared with another company until that company accepts the partner link.</p>

      {config && (
        <>
          <div className="grid grid-cols-3 gap-4 mb-8">
            {MODES.map(([v, title, sub]) => (
              <button key={v} data-testid={`mode-${v}`} onClick={() => setConfig({ ...config, ownership_mode: v })}
                className="rounded-lg border p-4 text-left transition-colors"
                style={config.ownership_mode === v ? { background: '#E7F2EE', borderColor: GREEN } : { background: 'white', borderColor: '#D9D6CD' }}>
                <p className="font-bold text-sm" style={{ color: '#1B1D1C' }}>
                  <span className="inline-block w-3 h-3 rounded-full border-2 mr-2 align-middle"
                    style={{ borderColor: config.ownership_mode === v ? GREEN : '#9a9e96', background: config.ownership_mode === v ? GREEN : 'white' }} />
                  {title}</p>
                <p className="text-xs mt-1" style={{ color: '#6b7069' }}>{sub}</p>
              </button>
            ))}
          </div>

          <div className="rounded-lg border bg-white p-6 mb-8" style={{ borderColor: '#E5E3DC' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg" style={{ color: '#1B1D1C' }}>Who sees what</h2>
              <Button data-testid="save-visibility-btn" onClick={save} className="text-white" style={{ background: GREEN }}>Save</Button>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
                <th className="py-2 font-medium">Party</th><th className="font-medium">Its own legs</th>
                <th className="font-medium">Journey shown</th><th className="font-medium">Locations</th>
                <th className="font-medium">Certificate</th></tr></thead>
              <tbody>
                {config.rules.map((r, i) => (
                  <tr key={r.party} className="border-b" style={{ borderColor: '#F3F1EA' }}>
                    <td className="py-3 font-bold pr-3" style={{ color: '#1B1D1C' }}>{PARTY_LABEL[r.party] || r.party}</td>
                    <td className="pr-2">{['RECEIVER', 'PUBLIC'].includes(r.party) ? <span style={{ color: '#9a9e96' }}>—</span>
                      : <Sel value={r.own_legs} options={OWN_LEGS} onChange={v => setRule(i, 'own_legs', v)} testid={`ownlegs-${r.party}`} />}</td>
                    <td className="pr-2"><Sel value={r.journey_shown} options={JOURNEY} onChange={v => setRule(i, 'journey_shown', v)} testid={`journey-${r.party}`} /></td>
                    <td className="pr-2"><Sel value={r.locations} options={LOCS} onChange={v => setRule(i, 'locations', v)} testid={`locations-${r.party}`} /></td>
                    <td><Sel value={r.certificate} options={CERTS} onChange={v => setRule(i, 'certificate', v)} testid={`cert-${r.party}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!templateId && <p className="text-sm mb-8" style={{ color: '#8C6A2B' }}>Pick a customer template on the Journey templates page to edit its visibility grid.</p>}

      <div className="rounded-lg border bg-white p-6" style={{ borderColor: '#E5E3DC' }} data-testid="partner-links-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg" style={{ color: '#1B1D1C' }}>Partner links</h2>
          <Button variant="outline" data-testid="invite-partner-btn" onClick={() => setShowInvite(!showInvite)}
            className="bg-white border-[#D9D6CD]">Invite partner</Button>
        </div>
        {showInvite && (
          <div className="rounded-md border p-4 mb-4 grid grid-cols-4 gap-3 items-end" style={{ borderColor: '#EDEBE4', background: '#FAF9F6' }}>
            <div>
              <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Partner type</label>
              <select data-testid="invite-kind-select" value={invite.mode} onChange={e => setInvite({ ...invite, mode: e.target.value })}
                className="w-full h-9 px-2 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
                <option value="tenant">Existing Track0tic customer</option>
                <option value="guest">Guest company (scan-only)</option>
              </select>
            </div>
            {invite.mode === 'tenant' ? (
              <div>
                <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Customer</label>
                <select data-testid="invite-tenant-select" value={invite.partner_tenant_id}
                  onChange={e => setInvite({ ...invite, partner_tenant_id: e.target.value })}
                  className="w-full h-9 px-2 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
                  <option value="">Choose…</option>
                  {customers.filter(c => c.id !== tenantId).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Company name</label>
                <Input data-testid="invite-guest-input" value={invite.guest_org_name}
                  onChange={e => setInvite({ ...invite, guest_org_name: e.target.value })} className="bg-white border-[#D9D6CD]" />
              </div>
            )}
            <div>
              <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Role in journey (stages)</label>
              <Input data-testid="invite-roles-input" value={invite.stage_roles}
                onChange={e => setInvite({ ...invite, stage_roles: e.target.value })}
                placeholder="CFA warehouse" className="bg-white border-[#D9D6CD]" />
            </div>
            <Button data-testid="send-invite-btn" onClick={sendInvite} className="text-white" style={{ background: GREEN }}>Send invite</Button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
            <th className="py-2 font-medium">Company</th><th className="font-medium">Role in journey</th>
            <th className="font-medium">On Track0tic as</th><th className="font-medium">Link status</th>
            <th className="font-medium">Agreed on</th><th></th></tr></thead>
          <tbody>
            {partners.map(p => (
              <tr key={p.id} className="border-b" style={{ borderColor: '#F3F1EA' }} data-testid={`partner-row-${p.id}`}>
                <td className="py-3">{p.partner_name}</td>
                <td>{(p.stage_roles || []).join(', ')}</td>
                <td>{p.partner_kind === 'TENANT' ? 'Customer (own devices)' : 'Guest (scan-only login)'}</td>
                <td><span className="px-2 py-0.5 rounded text-xs" style={
                  p.status === 'ACCEPTED' ? { background: '#E7F2EE', color: GREEN }
                    : p.status === 'INVITED' ? { background: '#FBF3E6', color: '#8C6A2B' }
                      : { background: '#FBEBEB', color: '#8C2B2B' }}>
                  {p.status === 'ACCEPTED' ? 'Accepted' : p.status === 'INVITED' ? 'Invited' : 'Revoked'}</span></td>
                <td>{p.accepted_at ? new Date(p.accepted_at).toLocaleDateString() : '—'}</td>
                <td className="text-right whitespace-nowrap">
                  {p.status !== 'REVOKED' && p.token && (
                    <button data-testid={`copy-scan-link-${p.id}`} className="text-xs underline mr-3" style={{ color: GREEN }}
                      onClick={() => {
                        const url = `${window.location.origin}/scan?invite=${p.token}`;
                        navigator.clipboard.writeText(url).then(() => toast.success('Scan link copied — one tap, no account needed'))
                          .catch(() => window.prompt('Copy this scan link:', url));
                      }}>Copy scan link</button>
                  )}
                  {p.status !== 'REVOKED' && <button data-testid={`revoke-${p.id}`} onClick={() => revoke(p)}
                    className="text-xs underline" style={{ color: '#8C2B2B' }}>Revoke</button>}
                </td>
              </tr>
            ))}
            {partners.length === 0 && <tr><td colSpan={6} className="py-6 text-center" style={{ color: '#9a9e96' }}>No partners yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
